import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  paymentApi,
  PaymentAllocationPreview,
  PaymentContext,
  PaymentResponse,
  UnpaidServiceFee,
} from '../../api/paymentApi';
import { printReceipt, ReceiptHtmlViewer } from '../receipt/ReceiptHtmlViewer';
import { parseBillingPeriodValue } from '../../utils/billingPeriodPicker';
import AddServiceFeeInvoiceLauncher from '../service-fees/AddServiceFeeInvoiceLauncher';
import { ViewApartmentFeeNoticeButton } from '../fee-notice/ViewApartmentFeeNoticeButton';
import {
  getPaymentFeeGroupKey,
  getPaymentFeeGroupLabel,
  comparePaymentFeeGroupOrder,
} from '../../utils/feeCategoryCodeGrouping';
import { getCasualParkingInvoiceContent } from '../../utils/casualParkingInvoice';
import { formatChargingInvoiceDetailLabel } from '../../utils/chargingInvoiceDisplay';
import { getInvoiceChainKey } from '../../utils/serviceFeeDebt';

interface ApartmentServiceFeePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  apartmentId: string;
  apartmentCode: string;
  onSuccess?: () => void;
  initialSelectedFeeIds?: string[];
  initialAmountReceived?: number;
  initialPaymentMethod?: 'CASH' | 'BANK_TRANSFER';
  initialPaymentDate?: string;
  initialProjectBankAccountId?: string;
  gatewayTransactionId?: string;
  /** Render inline on a page instead of overlay modal */
  embedded?: boolean;
}

type ModalStep = 'form' | 'preview' | 'success';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 }).format(Math.round(amount));

const parseCurrencyInput = (value: string) => Number(value.replace(/[^\d]/g, '')) || 0;

const formatCurrencyInput = (amount: number) =>
  amount > 0 ? formatCurrency(amount) : '';

const formatCurrencyOnType = (raw: string) => {
  const digits = raw.replace(/[^\d]/g, '');
  if (!digits) return '';
  return formatCurrency(Number(digits));
};

const compareBillingPeriods = (a: string, b: string): number => {
  const pa = parseBillingPeriodValue(a);
  const pb = parseBillingPeriodValue(b);
  if (pa.year !== pb.year) return pa.year - pb.year;
  return pa.month - pb.month;
};

const getCategoryKey = (fee: UnpaidServiceFee) => getPaymentFeeGroupKey(fee);

const toDateInputValue = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const computeAutoSurplusCredit = (
  surplusBalance: number,
  totalOwed: number,
  options?: { bankAmount?: number; fromReconciliation?: boolean },
): number => {
  if (surplusBalance <= 0 || totalOwed <= 0) return 0;
  if (options?.fromReconciliation && options.bankAmount != null) {
    return Math.min(surplusBalance, Math.max(0, totalOwed - options.bankAmount));
  }
  return Math.min(surplusBalance, totalOwed);
};

export const ApartmentServiceFeePaymentModal: React.FC<ApartmentServiceFeePaymentModalProps> = ({
  isOpen,
  onClose,
  apartmentId,
  apartmentCode,
  onSuccess,
  initialSelectedFeeIds,
  initialAmountReceived,
  initialPaymentMethod,
  initialPaymentDate,
  initialProjectBankAccountId,
  gatewayTransactionId,
  embedded = false,
}) => {
  const [step, setStep] = useState<ModalStep>('form');
  const [serviceFees, setServiceFees] = useState<UnpaidServiceFee[]>([]);
  const [selectedFees, setSelectedFees] = useState<string[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<'CASH' | 'BANK_TRANSFER'>('CASH');
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [paidBill, setPaidBill] = useState<PaymentResponse | null>(null);
  const [paymentContext, setPaymentContext] = useState<PaymentContext | null>(null);
  const [amountReceivedInput, setAmountReceivedInput] = useState('');
  const [creditAppliedInput, setCreditAppliedInput] = useState('');
  const [projectBankAccountId, setProjectBankAccountId] = useState('');
  const [paymentDate, setPaymentDate] = useState(toDateInputValue(new Date()));
  const [preview, setPreview] = useState<PaymentAllocationPreview | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [confirmedCollectionAmount, setConfirmedCollectionAmount] = useState<number | null>(null);
  const lastPreviewAmountReceived = useRef(0);
  const selectedFeesKey = useMemo(
    () => [...selectedFees].sort().join(','),
    [selectedFees],
  );

  const reloadUnpaidFees = async (preferredSelectedIds?: string[]) => {
    const unpaidData = await paymentApi.getUnpaidServiceFees(apartmentId);
    const list = unpaidData.fees || [];
    setServiceFees(list);
    const selectedIds =
      preferredSelectedIds && preferredSelectedIds.length > 0
        ? list.filter((fee) => preferredSelectedIds.includes(fee.id)).map((fee) => fee.id)
        : list.map((fee) => fee.id);
    setSelectedFees(selectedIds);
    const selectedTotal = list
      .filter((fee) => selectedIds.includes(fee.id))
      .reduce((sum, fee) => sum + fee.amount, 0);
    setAmountReceivedInput(formatCurrencyInput(selectedTotal || unpaidData.totalReceivable));
  };

  useEffect(() => {
    if ((!isOpen && !embedded) || !apartmentId) return;

    const loadData = async () => {
      setLoading(true);
      setPaidBill(null);
      setPreview(null);
      setPreviewHtml('');
      setStep('form');
      setPaymentNotes('');
      setConfirmedCollectionAmount(null);
      try {
        const unpaidData = await paymentApi.getUnpaidServiceFees(apartmentId);
        const list = unpaidData.fees || [];
        setServiceFees(list);
        const selectedIds =
          initialSelectedFeeIds && initialSelectedFeeIds.length > 0
            ? list.filter((fee) => initialSelectedFeeIds.includes(fee.id)).map((fee) => fee.id)
            : list.map((fee) => fee.id);
        setSelectedFees(selectedIds);
        const context = await paymentApi.getPaymentContext(apartmentId, selectedIds);
        setPaymentContext(context);
        const lockedAccountId =
          context.lockedProjectBankAccountId ||
          context.defaultProjectBankAccountId ||
          '';
        setProjectBankAccountId(
          context.isBankAccountLocked
            ? lockedAccountId
            : initialProjectBankAccountId || lockedAccountId,
        );
        setPaymentMethod(initialPaymentMethod || 'CASH');
        const surplus = context.surplusBalance || 0;
        const selectedTotal = list
          .filter((fee) => selectedIds.includes(fee.id))
          .reduce((sum, fee) => sum + fee.amount, 0);
        const total = selectedTotal || unpaidData.totalReceivable;
        const fromReconciliation = Boolean(gatewayTransactionId);
        const credit = computeAutoSurplusCredit(surplus, total, {
          bankAmount: initialAmountReceived,
          fromReconciliation,
        });
        const cashAmount =
          fromReconciliation && initialAmountReceived != null
            ? initialAmountReceived
            : Math.max(0, total - credit);
        setCreditAppliedInput(formatCurrencyInput(credit));
        setAmountReceivedInput(formatCurrencyInput(cashAmount));
        setPaymentDate(
          initialPaymentDate || toDateInputValue(new Date()),
        );
      } catch (error) {
        console.error('Error loading payment data:', error);
        toast.error('Không thể tải dữ liệu thanh toán');
        setServiceFees([]);
        setSelectedFees([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [
    isOpen,
    embedded,
    apartmentId,
    initialSelectedFeeIds,
    initialAmountReceived,
    initialPaymentMethod,
    initialPaymentDate,
    initialProjectBankAccountId,
    gatewayTransactionId,
  ]);

  useEffect(() => {
    if ((!isOpen && !embedded) || !apartmentId || loading) return;

    let cancelled = false;
    const refreshProjectAccountBinding = async () => {
      try {
        const context = await paymentApi.getPaymentContext(
          apartmentId,
          selectedFees.length > 0 ? selectedFees : undefined,
        );
        if (cancelled) return;
        setPaymentContext(context);
        const suggestedAccountId =
          context.lockedProjectBankAccountId || context.defaultProjectBankAccountId || '';
        setProjectBankAccountId((prev) =>
          context.isBankAccountLocked ? suggestedAccountId : prev || suggestedAccountId,
        );
      } catch (error) {
        console.error('Error refreshing payment context:', error);
      }
    };

    refreshProjectAccountBinding();
    return () => {
      cancelled = true;
    };
  }, [selectedFeesKey, apartmentId, isOpen, embedded, loading]);

  const groupedFees = useMemo(() => {
    const groups = new Map<string, { label: string; fees: UnpaidServiceFee[] }>();
    serviceFees.forEach((fee) => {
      const key = getCategoryKey(fee);
      if (!groups.has(key)) {
        groups.set(key, { label: getPaymentFeeGroupLabel(fee), fees: [] });
      }
      groups.get(key)!.fees.push(fee);
    });
    const result = Array.from(groups.values());
    result.forEach((group) => {
      group.fees.sort((a: UnpaidServiceFee, b: UnpaidServiceFee) =>
        compareBillingPeriods(a.billingPeriod, b.billingPeriod),
      );
    });
    result.sort(comparePaymentFeeGroupOrder);
    return result;
  }, [serviceFees]);

  const totalOwed = useMemo(
    () => serviceFees.filter((fee) => selectedFees.includes(fee.id)).reduce((sum, fee) => sum + fee.amount, 0),
    [serviceFees, selectedFees],
  );

  /** When selected invoices change, default cash + cấn trừ to fully cover Tổng phải thu (unless bank reconciliation). */
  useEffect(() => {
    if ((!isOpen && !embedded) || loading || !paymentContext) return;
    if (Boolean(gatewayTransactionId)) return;

    const surplus = paymentContext.surplusBalance || 0;
    const credit = computeAutoSurplusCredit(surplus, totalOwed, { fromReconciliation: false });
    if (surplus > 0 && totalOwed > 0) {
      setCreditAppliedInput(formatCurrencyInput(credit));
    }
    setAmountReceivedInput(formatCurrencyInput(Math.max(0, totalOwed - credit)));
  }, [
    isOpen,
    embedded,
    loading,
    paymentContext,
    selectedFeesKey,
    totalOwed,
    gatewayTransactionId,
  ]);

  const amountReceived = parseCurrencyInput(amountReceivedInput);
  const creditApplied = parseCurrencyInput(creditAppliedInput);
  const surplusBalance = paymentContext?.surplusBalance || 0;

  const selectedInvoicesTotal = preview?.totalOwed ?? totalOwed;
  const selectedInvoicesCashAmount = Math.max(0, selectedInvoicesTotal - creditApplied);

  const allocationMap = useMemo(() => {
    const map = new Map<string, number>();
    preview?.allocations.forEach((item) => map.set(item.serviceFeeId, item.cashAllocated ?? item.amountAllocated));
    return map;
  }, [preview]);

  const creditAllocationMap = useMemo(() => {
    const map = new Map<string, number>();
    preview?.allocations.forEach((item) => map.set(item.serviceFeeId, item.creditAllocated ?? 0));
    return map;
  }, [preview]);

  useEffect(() => {
    if (!isOpen && !embedded) {
      setPreview(null);
      return;
    }
    if (selectedFees.length === 0) {
      if (amountReceived > 0 && creditApplied <= 0) {
        setPreview({
          totalOwed: 0,
          amountReceived,
          creditApplied: 0,
          appliedAmount: 0,
          overpaymentAmount: amountReceived,
          remainingDebt: 0,
          allocations: [],
        });
      } else {
        setPreview(null);
      }
      return;
    }
    const timer = setTimeout(() => {
      paymentApi
        .previewPaymentAllocation({ serviceFeesIds: selectedFees, amountReceived, creditApplied })
        .then(setPreview)
        .catch(() => setPreview(null));
    }, 300);
    return () => clearTimeout(timer);
  }, [isOpen, embedded, selectedFees, amountReceived, creditApplied]);

  const getChainFeesSorted = (fee: UnpaidServiceFee): UnpaidServiceFee[] =>
    serviceFees
      .filter((f) => getInvoiceChainKey(f) === getInvoiceChainKey(fee))
      .sort((a, b) => compareBillingPeriods(a.billingPeriod, b.billingPeriod));

  /** Same kỳ: pick any invoice; older kỳ in this vehicle/meter chain must be selected first. */
  const isFeeSelectable = (fee: UnpaidServiceFee): boolean => {
    const feesInEarlierPeriods = getChainFeesSorted(fee).filter(
      (f) => compareBillingPeriods(f.billingPeriod, fee.billingPeriod) < 0,
    );
    return feesInEarlierPeriods.every((f) => selectedFees.includes(f.id));
  };

  const toggleFeeSelection = (feeId: string, checked: boolean) => {
    const fee = serviceFees.find((f) => f.id === feeId);
    if (!fee) return;

    const chainFees = getChainFeesSorted(fee);
    const period = fee.billingPeriod;

    if (checked) {
      setSelectedFees((prev) => Array.from(new Set([...prev, feeId])));
    } else {
      const toRemove = new Set<string>([feeId]);
      chainFees
        .filter((f) => compareBillingPeriods(f.billingPeriod, period) > 0)
        .forEach((f) => toRemove.add(f.id));
      setSelectedFees((prev) => prev.filter((id) => !toRemove.has(id)));
    }
  };

  const toggleCategorySelection = (fees: UnpaidServiceFee[], checked: boolean) => {
    if (checked) {
      setSelectedFees((prev) => Array.from(new Set([...prev, ...fees.map((f) => f.id)])));
    } else {
      const ids = fees.map((f) => f.id);
      setSelectedFees((prev) => prev.filter((id) => !ids.includes(id)));
    }
  };

  const groupFeesByBillingPeriod = (fees: UnpaidServiceFee[]) => {
    const map = new Map<string, UnpaidServiceFee[]>();
    fees.forEach((fee) => {
      const list = map.get(fee.billingPeriod) ?? [];
      list.push(fee);
      map.set(fee.billingPeriod, list);
    });
    return Array.from(map.entries()).sort(([a], [b]) => compareBillingPeriods(a, b));
  };

  const togglePeriodSelection = (periodFees: UnpaidServiceFee[], checked: boolean) => {
    if (checked) {
      setSelectedFees((prev) => Array.from(new Set([...prev, ...periodFees.map((f) => f.id)])));
    } else {
      const remove = new Set(periodFees.map((f) => f.id));
      setSelectedFees((prev) => prev.filter((id) => !remove.has(id)));
    }
  };

  const applyFullSurplus = () => {
    const maxCredit = Math.min(surplusBalance, Math.max(0, totalOwed - amountReceived));
    setCreditAppliedInput(formatCurrencyInput(maxCredit));
  };

  const applyRemainingDebtAmount = () => {
    const cashToCollect = Math.max(0, totalOwed - creditApplied);
    setAmountReceivedInput(formatCurrencyInput(cashToCollect));
  };

  const displayedRemainingDebt =
    preview != null
      ? preview.remainingDebt
      : Math.max(0, totalOwed - amountReceived - creditApplied);

  const handleOpenPreview = async (overrideAmountReceived?: number) => {
    const effectiveAmountReceived = overrideAmountReceived ?? amountReceived;
    if (selectedFees.length === 0) {
      if (effectiveAmountReceived <= 0) {
        toast.error('Vui lòng nhập số tiền thu');
        return;
      }
      if (creditApplied > 0) {
        toast.error('Không thể cấn trừ khi chưa chọn hóa đơn');
        return;
      }
    } else if (effectiveAmountReceived <= 0 && creditApplied <= 0) {
      toast.error('Vui lòng nhập số tiền thu hoặc cấn trừ');
      return;
    }
    if (overrideAmountReceived != null) {
      setAmountReceivedInput(formatCurrencyInput(overrideAmountReceived));
    }
    lastPreviewAmountReceived.current = effectiveAmountReceived;
    setConfirmedCollectionAmount(effectiveAmountReceived);

    setPreviewLoading(true);
    try {
      const { html } = await paymentApi.previewPaymentReceipt({
        apartmentId,
        serviceFeesIds: selectedFees,
        amountReceived: effectiveAmountReceived,
        creditApplied,
        paymentMethod,
        paymentDate,
        projectBankAccountId: projectBankAccountId || undefined,
      });
      setPreviewHtml(html);
      setStep('preview');
    } catch (error) {
      console.error('Preview receipt error:', error);
      toast.error('Không thể tạo xem trước phiếu thu');
    } finally {
      setPreviewLoading(false);
    }
  };

  const dismissSuccessView = async () => {
    onSuccess?.();
    if (embedded) {
      setPaidBill(null);
      setPreviewHtml('');
      setStep('form');
      await reloadUnpaidFees([]);
    } else {
      onClose();
    }
  };

  const handleConfirmPayment = async () => {
    const effectiveAmountReceived = lastPreviewAmountReceived.current || amountReceived;
    setProcessing(true);
    try {
      const result = await paymentApi.processPayment({
        serviceFeesIds: selectedFees,
        apartmentId: selectedFees.length === 0 ? apartmentId : undefined,
        paymentMethod,
        totalAmount: totalOwed,
        amountReceived: effectiveAmountReceived,
        creditApplied: selectedFees.length === 0 ? 0 : creditApplied,
        projectBankAccountId: projectBankAccountId || undefined,
        paymentDate,
        gatewayTransactionId,
        notes: paymentNotes.trim() || undefined,
      });
      toast.success('Thanh toán thành công!');
      setPaidBill(result);
      setStep('success');
    } catch (error: unknown) {
      console.error('Error processing payment:', error);
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Lỗi khi xử lý thanh toán');
    } finally {
      setProcessing(false);
    }
  };

  if (!isOpen && !embedded) return null;

  const shellClass = embedded
    ? 'bg-white rounded-xl shadow-sm border border-gray-200 w-full'
    : 'bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[92vh] overflow-y-auto';

  const wrapperClass = embedded
    ? ''
    : 'fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4';

  return (
    <div className={wrapperClass}>
      <div className={shellClass}>
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {step === 'preview'
              ? 'Xem trước phiếu thu'
              : step === 'success'
                ? 'Phiếu thu'
                : `Thanh toán phí - ${apartmentCode}`}
          </h2>
          <div className="flex items-center gap-2">
            {step === 'form' && (
              <>
                <ViewApartmentFeeNoticeButton
                  apartmentId={apartmentId}
                  apartmentCode={apartmentCode}
                  className="px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded disabled:opacity-50"
                />
                <AddServiceFeeInvoiceLauncher
                  apartmentId={apartmentId}
                  buildingId={paymentContext?.apartment.buildingId}
                  onAdded={async () => {
                    await reloadUnpaidFees();
                    onSuccess?.();
                  }}
                />
              </>
            )}
            {!embedded && (
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
            )}
          </div>
        </div>

        <div className={`p-6 space-y-6 ${embedded ? '' : ''}`}>
          {step === 'success' && paidBill ? (
            <>
              <div className="text-center space-y-2 mb-4">
                <div className="text-green-600 font-semibold text-lg">Thanh toán thành công</div>
                <p className="text-gray-600">
                  Mã giao dịch: <span className="font-medium">{paidBill.transactionCode}</span>
                </p>
              </div>
              {paidBill.billId ? (
                <div className="border rounded-lg overflow-hidden bg-gray-100" style={{ minHeight: '60vh' }}>
                  <ReceiptHtmlViewer billId={paidBill.billId} minHeight="60vh" />
                </div>
              ) : null}
              <div className="flex justify-center gap-3 mt-4">
                {paidBill.billId && (
                  <button
                    onClick={() => printReceipt(paidBill.billId!)}
                    className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded"
                  >
                    In phiếu thu
                  </button>
                )}
                <button
                  onClick={() => void dismissSuccessView()}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                >
                  Đóng
                </button>
              </div>
            </>
          ) : step === 'preview' ? (
            <>
              <div className="border rounded-lg overflow-hidden bg-gray-100" style={{ minHeight: '60vh' }}>
                {previewHtml ? (
                  <iframe
                    title="Xem trước phiếu thu"
                    srcDoc={previewHtml}
                    className="w-full border-0 bg-white"
                    style={{ minHeight: '60vh', display: 'block' }}
                    sandbox="allow-same-origin allow-scripts"
                  />
                ) : (
                  <div className="flex justify-center py-16 text-gray-500">Không có nội dung xem trước</div>
                )}
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    lastPreviewAmountReceived.current = 0;
                    setConfirmedCollectionAmount(null);
                    setStep('form');
                  }}
                  className="px-4 py-2 border rounded-lg"
                >
                  Quay lại
                </button>
                <button
                  type="button"
                  onClick={handleConfirmPayment}
                  disabled={processing}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-lg disabled:opacity-50"
                >
                  {processing
                    ? 'Đang xử lý...'
                    : `Xác nhận thu ${formatCurrency(confirmedCollectionAmount ?? amountReceived)}đ`}
                </button>
              </div>
            </>
          ) : loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
            </div>
          ) : (
            <>
              {serviceFees.length === 0 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Không có hóa đơn chưa thanh toán. Nhập số tiền và bấm{' '}
                  <strong>Thu</strong> để tạo phiếu thu dư — số tiền sẽ được ghi vào tài khoản căn hộ để
                  cấn trừ các kỳ phí sau.
                </div>
              ) : null}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-gray-600">Nhập nhanh số tiền thu lần này</label>
                  <div className="flex gap-2 mt-1">
                    <input
                      value={amountReceivedInput}
                      onChange={(e) => setAmountReceivedInput(formatCurrencyOnType(e.target.value))}
                      placeholder="0"
                      className="w-full rounded-lg border px-3 py-2 text-sm"
                    />
                    <button
                      type="button"
                      onClick={applyRemainingDebtAmount}
                      disabled={totalOwed <= 0}
                      className="px-3 py-2 bg-gray-50 border border-gray-300 text-gray-700 rounded-lg text-sm whitespace-nowrap hover:bg-gray-100 disabled:opacity-50"
                    >
                      Tính lại
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Cấn trừ từ tài khoản căn hộ</label>
                  <div className="flex gap-2 mt-1">
                    <input
                      value={creditAppliedInput}
                      onChange={(e) => setCreditAppliedInput(formatCurrencyOnType(e.target.value))}
                      placeholder="0"
                      disabled={selectedFees.length === 0}
                      className="w-full rounded-lg border px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400"
                    />
                    <button
                      type="button"
                      onClick={applyFullSurplus}
                      disabled={surplusBalance <= 0 || selectedFees.length === 0}
                      className="px-3 py-2 bg-cyan-50 border border-cyan-300 text-cyan-700 rounded-lg text-sm whitespace-nowrap hover:bg-cyan-100 disabled:opacity-50"
                    >
                      Chọn tất cả số dư
                    </button>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Số dư khả dụng: {formatCurrency(surplusBalance)}đ
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Ngày thanh toán</label>
                  <input
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    className="w-full rounded-lg border px-3 py-2 text-sm mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Hình thức thanh toán</label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value as 'CASH' | 'BANK_TRANSFER')}
                    className="w-full rounded-lg border px-3 py-2 text-sm mt-1"
                  >
                    <option value="CASH">Tiền mặt</option>
                    <option value="BANK_TRANSFER">Chuyển khoản</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Tài khoản dự án</label>
                  {paymentContext?.isBankAccountLocked ? (
                    <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm mt-1 text-gray-700">
                      {paymentContext.projectBankAccounts.find((account) => account.id === projectBankAccountId)?.name ||
                        'Tài khoản mặc định'}
                    </div>
                  ) : (
                    <select
                      value={projectBankAccountId}
                      onChange={(e) => setProjectBankAccountId(e.target.value)}
                      className="w-full rounded-lg border px-3 py-2 text-sm mt-1"
                    >
                      <option value="">Tự động chọn</option>
                      {(paymentContext?.projectBankAccounts || []).map((account) => (
                        <option key={account.id} value={account.id}>{account.name}</option>
                      ))}
                    </select>
                  )}
                  {paymentContext?.isBankAccountLocked ? (
                    <div className="text-xs text-gray-500 mt-1">
                      Tài khoản được gán tự động theo cấu hình loại thu / tài khoản mặc định.
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500 mt-1">
                      Loại thu Hoạt động chưa gán tài khoản — chọn tài khoản dự án khi thu.
                    </div>
                  )}
                </div>
              </div>

              {serviceFees.length > 0 ? (
              <div className="space-y-4">
                {groupedFees.map((group) => {
                  const allSelected = group.fees.every((f) => selectedFees.includes(f.id));
                  const someSelected = group.fees.some((f) => selectedFees.includes(f.id));
                  return (
                    <div key={group.label} className="border rounded-lg overflow-hidden">
                      <div className="bg-cyan-50 px-4 py-3 flex items-center justify-between border-b">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            ref={(el) => {
                              if (el) el.indeterminate = someSelected && !allSelected;
                            }}
                            onChange={(e) => toggleCategorySelection(group.fees, e.target.checked)}
                          />
                          <span className="font-semibold text-gray-900">{group.label}</span>
                        </div>
                        <span className="text-sm text-gray-600">
                          {groupFeesByBillingPeriod(group.fees).length} kỳ · {group.fees.length} hóa đơn
                        </span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50 text-xs text-gray-600">
                            <tr>
                              <th className="px-3 py-2 w-10" />
                              <th className="px-3 py-2 text-left">Kỳ hóa đơn</th>
                              <th className="px-3 py-2 text-right">Nợ đầu kỳ</th>
                              <th className="px-3 py-2 text-right">Phát sinh</th>
                              <th className="px-3 py-2 text-right">Đã TT</th>
                              <th className="px-3 py-2 text-right">Còn nợ</th>
                              <th className="px-3 py-2 text-right">Chi tiết</th>
                              <th className="px-3 py-2 text-right">Nhận</th>
                              <th className="px-3 py-2 text-right">Đã cấn trừ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupFeesByBillingPeriod(group.fees).flatMap(([period, periodFees]) => {
                              const periodAllSelected = periodFees.every((f) =>
                                selectedFees.includes(f.id),
                              );
                              const periodSomeSelected = periodFees.some((f) =>
                                selectedFees.includes(f.id),
                              );
                              const periodHeader =
                                periodFees.length > 1
                                  ? [
                                      <tr key={`${group.label}-${period}-header`} className="bg-slate-50/90">
                                        <td className="px-3 py-2">
                                          <input
                                            type="checkbox"
                                            checked={periodAllSelected}
                                            ref={(el) => {
                                              if (el) {
                                                el.indeterminate =
                                                  periodSomeSelected && !periodAllSelected;
                                              }
                                            }}
                                            onChange={(e) =>
                                              togglePeriodSelection(periodFees, e.target.checked)
                                            }
                                          />
                                        </td>
                                        <td className="px-3 py-2 font-medium text-slate-800" colSpan={8}>
                                          Kỳ {period} · {periodFees.length} hóa đơn ·{' '}
                                          {formatCurrency(
                                            periodFees.reduce((sum, f) => sum + f.amount, 0),
                                          )}
                                          đ
                                        </td>
                                      </tr>,
                                    ]
                                  : [];
                              const rows = periodFees.map((fee) => {
                              const selected = selectedFees.includes(fee.id);
                              const selectable = isFeeSelectable(fee);
                              const allocated = allocationMap.get(fee.id) ?? 0;
                              const creditAllocated = creditAllocationMap.get(fee.id) ?? 0;
                              const detailParts: string[] = [];
                              if (fee.meterCode) {
                                const meterLabel =
                                  fee.feeMultiplier != null && fee.feeMultiplier > 0
                                    ? `${fee.meterCode} (ĐKĐM: ${fee.feeMultiplier})`
                                    : fee.meterCode;
                                detailParts.push(`ĐH: ${meterLabel}`);
                              }
                              if (fee.usage != null && fee.invoiceType === 'USAGE_BASED') {
                                detailParts.push(`SL: ${fee.usage}`);
                                if (fee.startReading != null && fee.endReading != null) {
                                  detailParts.push(`${fee.startReading}→${fee.endReading}`);
                                }
                              } else if (fee.quantity > 1) {
                                detailParts.push(`SL: ${fee.quantity}`);
                              }
                              if (fee.licensePlate) detailParts.push(fee.licensePlate);
                              if (fee.invoiceType === 'CASUAL_PARKING') {
                                detailParts.push(getCasualParkingInvoiceContent(fee));
                                if (fee.machineAmount != null && fee.machineAmount > 0) {
                                  detailParts.push(`Máy: ${formatCurrency(fee.machineAmount)}`);
                                }
                                for (const v of fee.casualParkingVariances ?? []) {
                                  const tag = v.varianceType === 'VOUCHER' ? 'Voucher' : 'Khấu trừ';
                                  detailParts.push(
                                    `${tag} ${v.counterpartyName}: ${formatCurrency(v.amount)}`,
                                  );
                                }
                              } else {
                                const chargingDetail = formatChargingInvoiceDetailLabel({
                                  notes: fee.notes,
                                  isAutoGenerated: fee.isAutoGenerated,
                                  createdAt: fee.createdAt,
                                  feeCode: fee.feeCode,
                                  categoryName: fee.categoryName,
                                  usage: fee.usage,
                                });
                                if (chargingDetail) {
                                  detailParts.push(chargingDetail);
                                } else if (fee.description) {
                                  detailParts.push(fee.description);
                                } else if (fee.notes && fee.notes !== fee.billingPeriod) {
                                  detailParts.push(fee.notes);
                                }
                              }

                              return (
                                <tr key={fee.id} className="border-t">
                                  <td className="px-3 py-2">
                                    <input
                                      type="checkbox"
                                      checked={selected}
                                      disabled={!selected && !selectable}
                                      onChange={(e) => toggleFeeSelection(fee.id, e.target.checked)}
                                    />
                                  </td>
                                  <td className="px-3 py-2 font-medium">{fee.billingPeriod}</td>
                                  <td className="px-3 py-2 text-right text-gray-600">
                                    {formatCurrency(fee.displayOpeningDebt ?? 0)}
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    {formatCurrency(fee.incurredAmount ?? 0)}
                                  </td>
                                  <td className="px-3 py-2 text-right text-gray-600">
                                    {formatCurrency(fee.paidAmount ?? 0)}
                                  </td>
                                  <td className="px-3 py-2 text-right font-medium text-red-600">
                                    {formatCurrency(fee.amount)}đ
                                  </td>
                                  <td className="px-3 py-2 text-right text-xs text-gray-500">
                                    {detailParts.join(' · ') || '—'}
                                  </td>
                                  <td className="px-3 py-2 text-right font-medium text-cyan-700">
                                    {selected ? `${formatCurrency(allocated)}đ` : '0đ'}
                                  </td>
                                  <td className="px-3 py-2 text-right font-medium text-emerald-700">
                                    {selected ? `${formatCurrency(creditAllocated)}đ` : '0đ'}
                                  </td>
                                </tr>
                              );
                            });
                              return [...periodHeader, ...rows];
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
              ) : null}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 bg-gray-50 border rounded-lg p-4 text-sm">
                <div>
                  <span className="text-gray-500">Tổng phải thu</span>
                  <div className="text-lg font-semibold">{formatCurrency(preview?.totalOwed ?? totalOwed)}đ</div>
                </div>
                <div>
                  <span className="text-gray-500">Thu</span>
                  <div className="text-lg font-semibold text-red-600">{formatCurrency(amountReceived)}đ</div>
                </div>
                <div>
                  <span className="text-gray-500">Đã cấn trừ</span>
                  <div className="text-lg font-semibold text-emerald-600">{formatCurrency(creditApplied)}đ</div>
                </div>
                <div>
                  <span className="text-gray-500">Đóng dư</span>
                  <div className="text-lg font-semibold text-green-600">
                    {formatCurrency(preview?.overpaymentAmount ?? 0)}đ
                  </div>
                </div>
                <div>
                  <span className="text-gray-500">Còn nợ</span>
                  <div className="text-lg font-semibold">{formatCurrency(displayedRemainingDebt)}đ</div>
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-600">Ghi chú</label>
                <textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="Ghi chú cho phiếu thu / thanh toán (nếu cần)"
                  rows={2}
                  className="w-full rounded-lg border px-3 py-2 text-sm mt-1"
                />
              </div>

              <div className="flex flex-wrap justify-end gap-3">
                {!embedded && (
                  <button onClick={onClose} className="px-4 py-2 border rounded-lg">Hủy bỏ</button>
                )}
                <button
                  type="button"
                  onClick={() => handleOpenPreview()}
                  disabled={previewLoading || amountReceived <= 0}
                  title="Thu theo số tiền nhập nhanh (có thể thu dư hoặc chỉ thu dư)"
                  className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-lg disabled:opacity-50"
                >
                  {previewLoading ? 'Đang tải...' : `Thu ${formatCurrency(amountReceived)}đ`}
                </button>
                <button
                  type="button"
                  onClick={() => handleOpenPreview(selectedInvoicesCashAmount)}
                  disabled={
                    previewLoading ||
                    selectedFees.length === 0 ||
                    selectedInvoicesTotal <= 0
                  }
                  title="Thu đúng tổng các hóa đơn đã chọn"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 rounded-lg disabled:opacity-50"
                >
                  {previewLoading ? 'Đang tải...' : `✓ Thu ${formatCurrency(selectedInvoicesTotal)}đ`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApartmentServiceFeePaymentModal;
